<template>
  <div class="pb-4 mb-4">
    <h4 class="pb-1 mt-1 mb-4">{{ $t('manuali_uso') }}</h4>
    <!-- Mostra l'ultimo manuale in una card e gli altri in una select -->
    <b-row v-if="manuals && manuals.length">
        <b-col cols="12" md="6">
          <b-card class="mb-3">
            <h5 class="mb-2">{{ $t('ultima_versione') }}</h5>
            <div v-if="latestGroup && latestGroup.length">
              <p class="mb-1"><strong>{{ labelForGroup(latestGroup) }}</strong></p>
              <p class="mb-2 text-muted">{{ latestGroup[0].description || '' }}</p>
                <div>
                  <div v-for="m in latestGroup" :key="m.id" class="mb-2">
                    <b-button variant="secondary" @click="downloadManual(m)" block>
                      {{ languageLabel(m) }}
                    </b-button>
                  </div>
                </div>
            </div>
            <div v-else>
              <p>{{ $t('nessun_manuale') }}</p>
            </div>
          </b-card>
        </b-col>

      <b-col cols="12" md="6">
        <b-card class="mb-3">
          <h5 class="mb-2">{{ $t('altre_versioni') }}</h5>
          <b-form-select :options="selectOptions" v-model="selectedRelease" class="mb-2" />
          <div>
            <div v-if="selectedGroup && selectedGroup.length">
              <b-button v-if="selectedGroup.length===1" variant="outline-secondary" @click="downloadManual(selectedGroup[0])">
                {{ $t('scarica_selezionato') }}
              </b-button>
                <div v-else>
                  <div v-for="m in selectedGroup" :key="m.id" class="mb-2">
                    <b-button variant="outline-secondary" @click="downloadManual(m)" block>
                      {{ languageLabel(m) }}
                    </b-button>
                  </div>
                </div>
            </div>
            <b-button v-else :disabled="true" variant="outline-secondary">{{ $t('scarica_selezionato') }}</b-button>
          </div>
        </b-card>
      </b-col>
    </b-row>
    <!-- Fallback: mantieni i vecchi link env se l'API non restituisce nulla -->
    <b-row v-else>
      <b-col>
        <h5>
          <a :href="manualeUrl" target="_blank" class="text-secondary point" v-if="manualeUrl">
            <u>{{ $t('scarica_versione_aggiornata') }}</u>
          </a>
        </h5>
        <h5 v-if="manualeUrlEnglish">
          <a :href="manualeUrlEnglish" target="_blank" class="text-secondary point">
            <u>{{ $t('scarica_versione_inglese') }}</u>
          </a>
        </h5>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { utilityService } from "@/_services/utility.service";
export default {
  name: "GuidaCittadino",
  data() {
    return {
      manualeUrl: process.env.VUE_APP_URL_MANUALE_PAZIENTE,
      manualeUrlEnglish: process.env.VUE_APP_URL_MANUALE_PAZIENTE_ENG,
      manuals: [],
      selectedRelease: null,
    };
  },
  mounted() {
    // Carica tutti i manuali per paziente; il backend ha il filtro is_operatore/is_italian
    utilityService
      .getManuals({ is_operatore: false })
      .then((res) => {
        const data = res?.data?.data || [];
        const arr = Array.isArray(data) ? data : [];
        // normalizziamo la data_release in un release_key (YYYY-MM-DD) per raggruppare indipendentemente dal formato
        const normalize = (d) => {
          if (!d) return null;
          const dt = new Date(d);
          if (isNaN(dt)) return String(d);
          return dt.toISOString().slice(0, 10);
        };
        this.manuals = arr.map((m) => ({ ...m, release_key: normalize(m.data_release) }));
      })
      .catch(() => {
        // In caso di errore, resta sul fallback
        this.manuals = [];
      });
  },
  methods: {
    labelFor(m) {
      const parts = [];
      // Versione
      if (m.version) parts.push(`${m.version}`);
      // Lingua
      parts.push(m.is_italian ? "IT" : "EN");
      // Data
      if (m.data_release) {
        const d = new Date(m.data_release);
        if (!isNaN(d)) {
          const dd = d.toLocaleDateString('it-IT');
          parts.push(dd);
        }
      }
      return parts.join(" • ");
    },
    linkFor(m) {
      if (!m || !m.download_link) return this.manualeUrl || '#';
      // se è già una URL assoluta
      try {
        const u = new URL(m.download_link);
        return m.download_link;
      } catch (e) {
        // percorso relativo: usiamo VUE_APP_BE + storage/
        const be = process.env.VUE_APP_BE || '';
        return `${be.replace(/\/$/, '')}/storage/${m.download_link.replace(/^\//, '')}`;
      }
    },
    downloadFilename(m) {
      if (!m || !m.download_link) return '';
      // suggeriamo come filename la versione + estensione
      const parts = (m.download_link || '').split('/');
      const original = parts[parts.length - 1] || m.version || 'manuale';
      // se è un docx o pdf manteniamo estensione
      return `${m.version || original}`;
    },
    downloadManual(m) {
      if (!m) return;
      const url = this.linkFor(m);
      // Forziamo il download aprendo in una nuova finestra se same-origin
      const a = document.createElement('a');
      a.href = url;
      a.target = '_blank';
      a.rel = 'noopener';
      a.download = this.downloadFilename(m) || '';
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    // ritorna array di manuali con la stessa release_key (possibilmente IT + EN)
    groupByRelease(releaseKey) {
      if (!releaseKey) return [];
      return this.manuals.filter((x) => x.release_key === releaseKey || x.data_release === releaseKey);
    },
    labelForGroup(group) {
      if (!group || !group.length) return '';
      const m = group[0];
      const parts = [];
      if (m.version) parts.push(`${m.version}`);
      if (m.data_release) {
        const d = new Date(m.data_release);
        if (!isNaN(d)) parts.push(d.toLocaleDateString('it-IT'));
      }
      return parts.join(' • ');
    },
    languageLabel(m) {
      return m.is_italian ? (this.$t ? this.$t('scarica_versione_aggiornata') : 'IT') : (this.$t ? this.$t('scarica_versione_inglese') : 'EN');
    },
  },
  computed: {
    // gruppo dell'ultima release (array di 1 o 2 elementi)
    latestGroup() {
      if (!this.manuals || !this.manuals.length) return [];
      // troviamo la release_key più recente normalizzando
      const releases = Array.from(new Set(this.manuals.map((m) => m.release_key).filter(Boolean)));
      if (!releases.length) return [];
      const latest = releases
        .map((r) => ({ r, d: new Date(r) }))
        .filter((x) => !isNaN(x.d))
        .sort((a, b) => b.d - a.d)[0];
      if (!latest) return [];
      return this.groupByRelease(latest.r);
    },
    selectOptions() {
      if (!this.manuals) return [];
      // raccogliamo release_key distinte ordinate per data desc
      const releases = Array.from(new Set(this.manuals.map((m) => m.release_key).filter(Boolean))).sort((a, b) => new Date(b) - new Date(a));
      const latest = releases.length ? releases[0] : null;
      const others = releases.filter((r) => r !== latest);
      return others.map((r) => {
        const grp = this.groupByRelease(r);
        const suffix = (grp[0] && grp[0].version && grp[0].version.split('_').pop()) || r;
        const langs = grp.map((m) => (m.is_italian ? 'IT' : 'EN')).join(' / ');
        return { value: r, text: `${suffix} (${langs})` };
      });
    },
    selectedGroup() {
      return this.groupByRelease(this.selectedRelease);
    }
  }
};
</script>
